import i18n from '@/i18n';
/**
 * 第三方主图图案名 ↔ 产品模板颜色匹配。
 *
 * 含中文颜色时：WS-69000-W-白色-001-002 / 69000-W-白色-001-002
 * → 中文颜色为「白色」，颜色编码为紧邻其左的「W」。
 * 无中文时仍按第二段：XS001-CC-1 → CC。
 *
 * 填模板时：精确中文色名 → 颜色编码（商家 SKU）→ 宽松别名；均未命中则报错不刊登。
 */

import { stripImageFileExtension } from '@/utils/imageUrlUtils';

const CJK_RE = /[\u4e00-\u9fff]/;
const NON_COLOR_LABEL_RE = /^(主图|细节图?|印花图?|详情图?|图片)/;

function asText(value) {
  return String(value || '').trim();
}

function splitSkuSegments(raw) {
  return asText(raw)
    .split('-')
    .map((part) => part.trim())
    .filter(Boolean);
}

function isColorLabelSegment(part) {
  const text = asText(part);
  return Boolean(text) && CJK_RE.test(text) && !NON_COLOR_LABEL_RE.test(text);
}

/**
 * 从图片名提取中文颜色及其左侧颜色编码。
 * @param {string} name
 * @returns {{ colorCode: string, colorLabel: string }}
 */
export function extractColorFromImageName(name) {
  const base = stripImageFileExtension(asText(name));
  const parts = splitSkuSegments(base);
  const idx = parts.findIndex((part) => isColorLabelSegment(part));
  if (idx < 1) return { colorCode: '', colorLabel: '' };
  return {
    colorCode: parts[idx - 1],
    colorLabel: parts[idx],
  };
}

/**
 * 按顺序从多个图片名取第一组有效中文颜色 + 颜色编码。
 * @param {Array<string|{ name?: string, fileName?: string }>} namesOrImages
 * @returns {{ colorCode: string, colorLabel: string }}
 */
export function extractColorFromImageNames(namesOrImages) {
  const list = Array.isArray(namesOrImages) ? namesOrImages : [];
  for (const entry of list) {
    const candidates = typeof entry === 'string' || entry == null
      ? [entry]
      : [entry.name, entry.fileName];
    for (const candidate of candidates) {
      const parsed = extractColorFromImageName(candidate);
      if (parsed.colorLabel || parsed.colorCode) return parsed;
    }
  }
  return { colorCode: '', colorLabel: '' };
}

/**
 * 从主图图案名称提取颜色编码。
 * 优先取中文颜色左侧色号；否则取第二段。
 * @param {string} name
 * @returns {string} 大写颜色码，无法提取时为空
 */
export function extractColorCodeFromPatternName(name) {
  const fromChinese = extractColorFromImageName(name);
  if (fromChinese.colorCode) return fromChinese.colorCode.toUpperCase();
  const parts = splitSkuSegments(stripImageFileExtension(asText(name)));
  if (parts.length < 2) return '';
  return parts[1].toUpperCase();
}

/**
 * 从商家 SKU 提取颜色编码（第二段）。
 * @param {string} supplierSku
 * @returns {string}
 */
export function extractColorCodeFromSupplierSku(supplierSku) {
  const parts = splitSkuSegments(supplierSku);
  if (parts.length < 2) return '';
  return parts[1].toUpperCase();
}

/**
 * 商家 SKU 是否包含指定颜色段（跳过货号首段）。
 * @param {string} supplierSku
 * @param {string} colorCode
 */
export function supplierSkuMatchesColor(supplierSku, colorCode) {
  const token = asText(colorCode).toUpperCase();
  if (!token) return false;
  const parts = splitSkuSegments(supplierSku);
  return parts.some((part, index) => index > 0 && part.toUpperCase() === token);
}

function collectSkcSupplierSkus(skc) {
  const rows = Array.isArray(skc?.skuRows) ? skc.skuRows : [];
  return rows.map((sku) => asText(sku?.supplierSku)).filter(Boolean);
}

function skcColorLabels(skc) {
  return [
    skc?.colorLabel,
    skc?.colorExtraValue,
    skc?.skcName,
  ].map(asText).filter(Boolean);
}

function normalizeColorMatchKey(text) {
  return asText(text)
    .toLowerCase()
    .replace(/\s+/g, '')
    .replace(/[&＋+]/g, '');
}

/**
 * 常见颜色的「中文名 + 英文名 + 商家 SKU 色号」同义组。
 * 仅收录同一颜色的等价写法，不合并深浅/拼色（如「浅灰」与「灰」不算同组），
 * 避免为了命中而把两个真实存在的颜色串成一个。
 */
const COLOR_ALIAS_GROUPS = [
  ['白色', '白', 'white', 'w', 'wh', 'wt'],
  ['黑色', '黑', 'black', 'bk', 'blk'],
  ['红色', '红', 'red', 'rd'],
  ['灰色', '灰', 'gray', 'grey', 'gy', 'gry'],
  ['粉色', '粉', 'pink', 'pk', 'pnk'],
  ['棕色', '棕', '咖啡', '咖啡色', 'brown', 'br', 'bn', 'brn', 'coffee'],
  ['米色', '米白', 'beige', 'be', 'bg', 'cream', 'ivory'],
  ['驼色', '驼', 'camel', 'cm'],
  ['紫色', '紫', 'purple', 'pur', 'pu', 'violet'],
  ['黄色', '黄', 'yellow', 'yl', 'ye'],
  ['橙色', '橙', '橘色', 'orange', 'or', 'og'],
  ['绿色', '绿', 'green', 'gn', 'grn'],
  ['蓝色', '蓝', 'blue', 'bl', 'bu'],
  ['酒红色', '酒红', 'wine', 'win', 'burgundy'],
  ['金色', '金', 'gold', 'gd'],
  ['银色', '银', 'silver', 'sl', 'sv'],
  ['多色', '彩色', '彩', '花色', 'multi', 'colorful'],
];

/** 常见中英文颜色别名；禁止用单字「白/黑/红」匹配「蓝拼白」这类复合色名。 */
function expandColorAliasKeys(label) {
  const raw = asText(label);
  const key = normalizeColorMatchKey(raw);
  if (!key) return [];
  const out = new Set([key]);
  const push = (...vals) => vals.forEach((v) => {
    const n = normalizeColorMatchKey(v);
    if (n) out.add(n);
  });
  if (/浅蓝|浅蓝白|蓝拼白|light\s*blue|lbw/i.test(raw)
    || key.includes('lightblue')
    || key === 'lbw'
    || key === '蓝拼白'
    || key === '浅蓝白') {
    push('浅蓝色', '浅蓝', '浅蓝白', '蓝拼白', 'lightblue', 'lightbluewhite', 'lbw');
  }
  if (/墨绿|墨绿拼米白|dark\s*green|ptb/i.test(raw)
    || key.includes('darkgreen')
    || key === 'ptb'
    || key === '墨绿拼米白') {
    push('墨绿拼米白', '墨绿色', 'darkgreen', 'darkgreencream', 'ptb');
  }
  if (/藏青拼米白|藏青|navy|nb\b/i.test(raw) || key === 'nb' || key === '藏青拼米白') {
    push('藏青拼米白', '藏青色', 'navy', 'nb');
  }
  // 按整名精确命中同义组：`米白` 不会落进白色组，`浅灰` 也不会落进灰色组
  for (const group of COLOR_ALIAS_GROUPS) {
    if (group.some((item) => normalizeColorMatchKey(item) === key)) {
      push(...group);
    }
  }
  return [...out];
}

/** 颜色编码是否等价：先精确，再按同义组比较（W / WH / WHITE、GY / GREY、BR / BROWN）。 */
export function colorCodesLooselyMatch(a, b) {
  const left = normalizeColorMatchKey(a);
  const right = normalizeColorMatchKey(b);
  if (!left || !right) return false;
  if (left === right) return true;
  const rightKeys = new Set(expandColorAliasKeys(b));
  return expandColorAliasKeys(a).some((key) => rightKeys.has(key));
}

/** 商家 SKU 里是否有等价颜色段（跳过货号首段）。 */
function supplierSkuHasLooseColor(supplierSku, colorCode) {
  const parts = splitSkuSegments(supplierSku);
  return parts.some((part, index) => index > 0 && colorCodesLooselyMatch(part, colorCode));
}

function colorLabelsExactMatch(a, b) {
  const left = normalizeColorMatchKey(a);
  const right = normalizeColorMatchKey(b);
  return Boolean(left && right && left === right);
}

/**
 * 颜色的「基础色」键：去掉深浅/明暗修饰与「色」字。
 * 「浅蓝色」「深蓝色」「蓝色」「Blue」→ 同一组；「蓝拼白」「墨绿拼米白」不受影响。
 * 只在前面几步全部落空时使用，避免深浅色被过早合并。
 */
function baseColorKeys(label) {
  const raw = asText(label);
  const out = new Set();
  if (!raw) return out;
  const candidates = [
    raw,
    raw.replace(/^(浅|深|淡|亮|暗)\s*/u, ''),           // 浅蓝色 / 深蓝
    raw.replace(/^(light|dark|pale|deep)[\s_-]*/i, ''),  // Light Blue / Dark-Green
  ];
  for (const candidate of candidates) {
    const key = normalizeColorMatchKey(candidate);
    if (!key) continue;
    out.add(key);
    if (key.length > 1 && key.endsWith('色')) out.add(key.slice(0, -1)); // 蓝色 → 蓝
  }
  return [...out];
}

function baseColorLabelsMatch(a, b) {
  const left = baseColorKeys(a);
  const right = new Set(baseColorKeys(b));
  if (!left.length || !right.size) return false;
  return left.some((key) => right.has(key));
}

function colorLabelsLooselyMatch(a, b) {
  if (colorLabelsExactMatch(a, b)) return true;
  const left = expandColorAliasKeys(a);
  const right = new Set(expandColorAliasKeys(b));
  if (!left.length || !right.size) return false;
  return left.some((key) => right.has(key));
}

/**
 * 在主规格（SKC 列表）中找到颜色与图案色号匹配的那一项。
 * 优先商家 SKU 色号段，其次主规格色号文本。
 * @param {object[]} skcList
 * @param {string} colorCode
 * @param {{ usedIndexes?: Set<number> }} [options] 已占用下标时优先下一个同色 SKC
 * @returns {number} 匹配下标，未命中为 -1
 */
export function findSkcIndexByColorCode(skcList, colorCode, options = {}) {
  const token = asText(colorCode).toUpperCase();
  if (!token) return -1;
  const list = Array.isArray(skcList) ? skcList : [];
  const used = options.usedIndexes instanceof Set ? options.usedIndexes : null;
  let fallback = -1;
  for (let i = 0; i < list.length; i += 1) {
    const skc = list[i];
    const skus = collectSkcSupplierSkus(skc);
    // 顺序：商家 SKU 色号精确 → 色号同义（W/WH/WHITE）→ 主规格色号文本 → 颜色名宽松
    const hit = skus.some((sku) => supplierSkuMatchesColor(sku, token))
      || skus.some((sku) => supplierSkuHasLooseColor(sku, token))
      || skcColorLabels(skc).some((label) => label.toUpperCase() === token)
      || skcColorLabels(skc).some((label) => colorLabelsLooselyMatch(label, token));
    if (!hit) continue;
    if (used && used.has(i)) {
      if (fallback < 0) fallback = i;
      continue;
    }
    return i;
  }
  return fallback;
}

/**
 * 按颜色名精确匹配 SKC（去空白大小写，不含别名）。
 * @param {object[]} skcList
 * @param {string} colorLabel
 * @param {{ usedIndexes?: Set<number> }} [options]
 * @returns {number}
 */
export function findSkcIndexByColorLabelExact(skcList, colorLabel, options = {}) {
  const token = asText(colorLabel);
  if (!token) return -1;
  const list = Array.isArray(skcList) ? skcList : [];
  const used = options.usedIndexes instanceof Set ? options.usedIndexes : null;
  let fallback = -1;
  for (let i = 0; i < list.length; i += 1) {
    const hit = skcColorLabels(list[i]).some((label) => colorLabelsExactMatch(label, token));
    if (!hit) continue;
    if (used && used.has(i)) {
      if (fallback < 0) fallback = i;
      continue;
    }
    return i;
  }
  return fallback;
}

/**
 * 按颜色名匹配 SKC 主规格颜色（支持中英文别名 / 去空白比对）。
 * @param {object[]} skcList
 * @param {string} colorLabel
 * @param {{ usedIndexes?: Set<number> }} [options]
 * @returns {number}
 */
export function findSkcIndexByColorLabel(skcList, colorLabel, options = {}) {
  const token = asText(colorLabel);
  if (!token) return -1;
  const list = Array.isArray(skcList) ? skcList : [];
  const used = options.usedIndexes instanceof Set ? options.usedIndexes : null;
  let fallback = -1;
  for (let i = 0; i < list.length; i += 1) {
    const hit = skcColorLabels(list[i]).some((label) => colorLabelsLooselyMatch(label, token));
    if (!hit) continue;
    if (used && used.has(i)) {
      if (fallback < 0) fallback = i;
      continue;
    }
    return i;
  }
  return fallback;
}

/**
 * 兜底：按基础色匹配（浅蓝色 ↔ 蓝色 ↔ 深蓝 ↔ Blue）。
 * 只有当模板里**恰好只有一个**候选色时才认，避免模板同时存在「蓝色 / 浅蓝色」时串色。
 * @param {object[]} skcList
 * @param {string} colorLabel
 * @param {{ usedIndexes?: Set<number> }} [options]
 * @returns {number}
 */
export function findSkcIndexByBaseColorLabel(skcList, colorLabel, options = {}) {
  const token = asText(colorLabel);
  if (!token) return -1;
  const list = Array.isArray(skcList) ? skcList : [];
  const used = options.usedIndexes instanceof Set ? options.usedIndexes : null;
  const hits = [];
  for (let i = 0; i < list.length; i += 1) {
    const matched = skcColorLabels(list[i]).some((label) => baseColorLabelsMatch(label, token));
    if (matched) hits.push(i);
  }
  const candidates = hits.filter((index) => !(used && used.has(index)));
  // 模板里同基础色多于一个（蓝色 + 浅蓝色）：无法判断该用哪个，宁可交给上层跳过也不串色
  if (candidates.length !== 1) return -1;
  return candidates[0];
}

/**
 * 精确中文 → 颜色编码 → 宽松别名 → 基础色（浅蓝↔蓝）兜底。
 * @param {object[]} skcList
 * @param {{ patternName?: string, colorCode?: string, colorLabel?: string }} [hints]
 * @param {{ usedIndexes?: Set<number> }} [options]
 * @returns {{ colorCode: string, colorLabel: string, skcIndex: number }}
 */
export function matchSkcByColorHints(skcList, hints = {}, options = {}) {
  const patternName = hints.patternName || hints.mainName || '';
  const parsed = extractColorFromImageName(patternName);
  const colorLabel = asText(hints.colorLabel) || parsed.colorLabel;
  const colorCode = asText(hints.colorCode) || extractColorCodeFromPatternName(patternName);
  let skcIndex = findSkcIndexByColorLabelExact(skcList, colorLabel, options);
  if (skcIndex < 0) {
    skcIndex = findSkcIndexByColorCode(skcList, colorCode, options);
  }
  if (skcIndex < 0) {
    skcIndex = findSkcIndexByColorLabel(skcList, colorLabel, options);
  }
  if (skcIndex < 0) {
    skcIndex = findSkcIndexByBaseColorLabel(skcList, colorLabel, options);
  }
  return { colorCode, colorLabel, skcIndex };
}

/**
 * 颜色未命中模板时的报错文案。
 * @param {{ colorCode?: string, colorLabel?: string, patternName?: string, mainName?: string }} slot
 * @param {string[]} [templateColors] 模板现有颜色，附在文案后便于直接对比排查
 * @returns {string}
 */
export function formatUnmatchedListingColorMessage(slot = {}, templateColors = []) {
  const label = asText(slot.colorLabel);
  const code = asText(slot.colorCode);
  const parts = [];
  if (label) parts.push(`中文「${label}」`);
  if (code) parts.push(`编码「${code}」`);
  if (!parts.length) {
    const name = asText(slot.patternName || slot.mainName);
    if (name) parts.push(`主图名「${name}」`);
  }
  const tip = parts.length ? parts.join('、') : '未提供中文颜色或颜色编码';
  const base = i18n.t('workbench:ipeUtilError.colorNotMatchedTemplate', { tip });
  const available = (Array.isArray(templateColors) ? templateColors : [])
    .map((item) => String(item || '').trim())
    .filter(Boolean);
  return available.length ? `${base}（模板颜色：${available.join('、')}）` : base;
}

/** 模板 skcList 里可用于排查的颜色名（中文 + 编码）。 */
export function collectTemplateColorLabels(skcList) {
  const out = [];
  (Array.isArray(skcList) ? skcList : []).forEach((skc) => {
    const labels = [
      skc?.colorLabel,
      skc?.colorExtraValue,
      extractColorCodeFromSupplierSku((Array.isArray(skc?.skuRows) ? skc.skuRows : [])[0]?.supplierSku),
    ].map(asText).filter(Boolean);
    labels.forEach((label) => {
      if (!out.includes(label)) out.push(label);
    });
  });
  return out;
}

/**
 * 从主图名解析颜色并定位 SKC。
 * 先中文颜色，再颜色编码（商家 SKU）兜底。
 * @param {object[]} skcList
 * @param {string} patternName
 * @param {{ usedIndexes?: Set<number> }} [options]
 * @returns {{ colorCode: string, colorLabel: string, skcIndex: number }}
 */
export function matchSkcByPatternName(skcList, patternName, options = {}) {
  return matchSkcByColorHints(skcList, { patternName }, options);
}

/**
 * 刊登色槽：优先槽位已解析的中文颜色 / 颜色编码，图案名作补充。
 * @param {object[]} skcList
 * @param {object} slot
 * @param {{ usedIndexes?: Set<number> }} [options]
 * @returns {number}
 */
export function resolveListingSlotSkcIndex(skcList, slot, options = {}) {
  return matchSkcByColorHints(skcList, {
    patternName: slot?.patternName || slot?.mainName,
    colorCode: slot?.colorCode,
    colorLabel: slot?.colorLabel,
  }, options).skcIndex;
}
